<template>
  <div class="schools-settings">
    <div class="header">
      <h2>Okul Yönetimi</h2>
      <div class="stats">
        Toplam: <strong>{{ totalCount }}</strong> okul
      </div>
    </div>

    <div class="search-bar">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Okul ara... (en az 2 karakter)"
        class="search-input"
        @input="onSearch"
      />
      <button class="add-btn" @click="showAddModal = true">+ Yeni Okul</button>
    </div>

    <div v-if="isLoading" class="loading">Yükleniyor...</div>

    <div v-else class="schools-table">
      <div v-if="!searchQuery && schools.length === 0" class="info-message">
        Okul aramak için yukarıdaki arama kutusunu kullanın
      </div>
      <table v-else>
        <thead>
          <tr>
            <th>Okul Adı</th>
            <th>İl</th>
            <th>İlçe</th>
            <th>Telefon</th>
            <th>İşlemler</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="school in schools" :key="school.id">
            <td>{{ school.kurum }}</td>
            <td>{{ school.il }}</td>
            <td>{{ school.ilce }}</td>
            <td>{{ school.telefon || '-' }}</td>
            <td>
              <button class="edit-btn" @click="editSchool(school)">Detay</button>
              <button class="delete-btn" @click="deleteSchool(school)">Sil</button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="schools.length >= 50" class="more-info">
        İlk 50 sonuç gösteriliyor. Daha spesifik arama yapın.
      </div>
    </div>

    <!-- Detail/Edit Modal -->
    <div v-if="showAddModal || editingSchool" class="modal-overlay" @click.self="closeModal">
      <div class="modal modal-wide">
        <h3>{{ editingSchool ? 'Okul Detayları' : 'Yeni Okul Ekle' }}</h3>

        <div class="modal-body">
          <div class="modal-section">
            <h4>Temel Bilgiler</h4>
            <div class="form-row">
              <div class="form-group flex-2">
                <label>Okul Adı</label>
                <input v-model="form.kurum" type="text" />
              </div>
              <div class="form-group">
                <label>Kurum Kodu</label>
                <input v-model="form.kurumkodu" type="text" readonly class="readonly" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>İl</label>
                <input v-model="form.il" type="text" />
              </div>
              <div class="form-group">
                <label>İlçe</label>
                <input v-model="form.ilce" type="text" />
              </div>
              <div class="form-group">
                <label>Kurum Türü</label>
                <input v-model="form.kurum_turu" type="text" readonly class="readonly" />
              </div>
            </div>
          </div>

          <div class="modal-section">
            <h4>İletişim Bilgileri</h4>
            <div class="form-row">
              <div class="form-group">
                <label>Telefon</label>
                <input v-model="form.telefon" type="text" />
              </div>
              <div class="form-group">
                <label>Fax</label>
                <input v-model="form.fax" type="text" />
              </div>
              <div class="form-group">
                <label>Web Sitesi</label>
                <input v-model="form.web" type="text" />
              </div>
            </div>
            <div class="form-group">
              <label>Adres</label>
              <textarea v-model="form.adres" rows="2"></textarea>
            </div>
          </div>

          <div class="modal-section" v-if="editingSchool">
            <h4>Notlar</h4>
            <div class="form-group">
              <textarea v-model="form.notes" rows="3" placeholder="Bu okul hakkında notlar..."></textarea>
            </div>
          </div>

          <div class="modal-section" v-if="editingSchool && linkedContacts.length > 0">
            <h4>Bağlantılı Kişiler ({{ linkedContacts.length }})</h4>
            <div class="contacts-list">
              <div v-for="contact in linkedContacts" :key="contact.id" class="contact-card">
                <div class="contact-name">{{ contact.name }}</div>
                <div class="contact-info">{{ contact.phone_number || contact.email }}</div>
              </div>
            </div>
          </div>
          <div class="modal-section" v-else-if="editingSchool">
            <h4>Bağlantılı Kişiler</h4>
            <div class="no-contacts">Bu okulla ilişkili kişi bulunmuyor</div>
          </div>
        </div>

        <div class="modal-actions">
          <button @click="closeModal">Kapat</button>
          <button class="save-btn" @click="saveSchool">Kaydet</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      schools: [],
      totalCount: 0,
      searchQuery: '',
      showAddModal: false,
      editingSchool: null,
      isLoading: false,
      searchTimeout: null,
      linkedContacts: [],
      form: {
        kurum: '', il: '', ilce: '', kurumkodu: '', kurum_turu: '',
        telefon: '', fax: '', web: '', adres: '', notes: ''
      }
    };
  },
  mounted() {
    this.fetchTotalCount();
  },
  methods: {
    async fetchTotalCount() {
      try {
        const res = await fetch('/api/v1/schools/count');
        const data = await res.json();
        this.totalCount = data.count || 0;
      } catch (e) {
        this.totalCount = '?';
      }
    },
    onSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.searchSchools();
      }, 300);
    },
    async searchSchools() {
      if (this.searchQuery.length < 2) {
        this.schools = [];
        return;
      }
      this.isLoading = true;
      try {
        const res = await fetch(`/api/v1/schools/search?q=${encodeURIComponent(this.searchQuery)}`);
        this.schools = await res.json();
      } catch (e) {
        this.schools = [];
      }
      this.isLoading = false;
    },
    async editSchool(school) {
      this.editingSchool = school;
      this.form = { ...school };
      // Fetch linked contacts
      try {
        const res = await fetch(`/api/v1/schools/${school.id}/contacts`);
        this.linkedContacts = await res.json();
      } catch (e) {
        this.linkedContacts = [];
      }
    },
    closeModal() {
      this.showAddModal = false;
      this.editingSchool = null;
      this.linkedContacts = [];
      this.form = { kurum: '', il: '', ilce: '', kurumkodu: '', kurum_turu: '',
                    telefon: '', fax: '', web: '', adres: '', notes: '' };
    },
    async saveSchool() {
      const url = this.editingSchool ? `/api/v1/schools/${this.editingSchool.id}` : '/api/v1/schools';
      const method = this.editingSchool ? 'PUT' : 'POST';
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ school: this.form })
      });
      this.closeModal();
      this.fetchTotalCount();
      if (this.searchQuery.length >= 2) this.searchSchools();
    },
    async deleteSchool(school) {
      if (!confirm(`"${school.kurum}" okulunu silmek istediğinize emin misiniz?`)) return;
      await fetch(`/api/v1/schools/${school.id}`, { method: 'DELETE' });
      this.fetchTotalCount();
      if (this.searchQuery.length >= 2) this.searchSchools();
    }
  }
};
</script>

<style scoped>
.schools-settings { padding: 24px; }
.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.header h2 { margin: 0; }
.search-bar { display: flex; gap: 12px; margin-bottom: 20px; }
.search-input { flex: 1; padding: 10px 16px; border: 1px solid #ddd; border-radius: 8px; font-size: 14px; }
.add-btn { padding: 10px 20px; background: #2563eb; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 500; }
.schools-table { background: white; max-height: 60vh; overflow-y: auto; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 12px 16px; text-align: left; border-bottom: 1px solid #eee; }
th { background: #f8f9fa; font-weight: 600; position: sticky; top: 0; }
.edit-btn { padding: 6px 12px; background: #f0f9ff; color: #0369a1; border: none; border-radius: 4px; cursor: pointer; margin-right: 8px; }
.delete-btn { padding: 6px 12px; background: #fef2f2; color: #dc2626; border: none; border-radius: 4px; cursor: pointer; }
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { background: white; padding: 24px; border-radius: 12px; width: 400px; max-height: 90vh; overflow-y: auto; }
.modal-wide { width: 700px; }
.modal h3 { margin: 0 0 20px; }
.modal-body { max-height: 60vh; overflow-y: auto; }
.modal-section { margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid #eee; }
.modal-section:last-child { border-bottom: none; }
.modal-section h4 { margin: 0 0 12px; font-size: 14px; color: #666; text-transform: uppercase; }
.form-row { display: flex; gap: 12px; margin-bottom: 12px; }
.form-group { flex: 1; margin-bottom: 12px; }
.form-group.flex-2 { flex: 2; }
.form-group label { display: block; margin-bottom: 6px; font-weight: 500; font-size: 13px; }
.form-group input, .form-group textarea { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; }
.form-group input.readonly { background: #f5f5f5; color: #666; }
.modal-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 20px; padding-top: 16px; border-top: 1px solid #eee; }
.modal-actions button { padding: 10px 20px; border-radius: 6px; cursor: pointer; border: 1px solid #ddd; background: white; }
.save-btn { background: #2563eb !important; color: white !important; border: none !important; }
.loading { text-align: center; padding: 40px; color: #666; }
.info-message { text-align: center; padding: 40px; color: #666; font-size: 16px; }
.more-info { text-align: center; padding: 12px; color: #888; font-size: 13px; background: #f8f9fa; }
.contacts-list { display: flex; flex-wrap: wrap; gap: 8px; }
.contact-card { background: #f0f9ff; padding: 8px 12px; border-radius: 6px; font-size: 13px; }
.contact-name { font-weight: 500; color: #0369a1; }
.contact-info { color: #666; font-size: 12px; }
.no-contacts { color: #888; font-style: italic; font-size: 13px; }
</style>
